import { SendMessageModal } from "@/components/mentor-performance/send-message-modal";
import {
  formatDecimal,
  formatMentorTenure,
  formatPercent,
  formatResponseTime,
} from "@/components/mentor-performance/metric-formatters";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  MentorMetricsRow,
  MentorMetricsSort,
  MentorMetricsSortField,
  MentorMetricsTablePage,
  nextMentorMetricsSort,
} from "@/lib/api/mentor-metrics-dashboard";
import { useMentorPerformanceEvent } from "@/lib/hooks/mentor-performance/use-mentor-performance-event";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { PostHogEvents } from "@/lib/posthog-events";
import { cn, getInitials } from "@/lib/utils";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { Moodies } from "@somethings-ui/core/assets";
import { Button, IconButton } from "@somethings-ui/web";
import { Link } from "@tanstack/react-router";
import { Loader2, Send } from "lucide-react";
import { useState } from "react";

interface MentorsTableProps {
  data: MentorMetricsTablePage | undefined;
  isLoading: boolean;
  isRefreshing?: boolean;
  isError: boolean;
  sort: MentorMetricsSort[];
  page: number;
  pageSize: number;
  pageSizes: readonly number[];
  onSortChange: (sort: MentorMetricsSort[]) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const COLUMNS: { label: string; field: MentorMetricsSortField }[] = [
  { label: "MENTOR", field: "mentor_name" },
  { label: "TENURE", field: "tenure" },
  { label: "PROFILE SCORE", field: "appeal_score" },
  { label: "ACTIVE CASELOAD", field: "active_caseload" },
  { label: "MEDIAN RESPONSE TIME", field: "average_response_time" },
  { label: "D3 RETENTION", field: "d3_retention" },
];

const isNewHire = (mentor: MentorMetricsRow) =>
  mentor.tenureDays >= 1 && mentor.tenureDays <= 30;

function LowDataValue({
  children,
  tooltip = "Low data",
}: {
  children: string;
  tooltip?: string;
}) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger className="cursor-help">{children}</TooltipTrigger>
        <TooltipContent className="bg-black text-xs text-white">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ProfileScore({
  mentor,
  available,
}: {
  mentor: MentorMetricsRow;
  available: boolean;
}) {
  if (!available) {
    return (
      <LowDataValue tooltip="Profile Score is temporarily unavailable">
        Unavailable
      </LowDataValue>
    );
  }

  if (mentor.appealScore === null || !mentor.appealReliable) {
    return <LowDataValue>—</LowDataValue>;
  }

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger className="cursor-help">
          {formatDecimal(mentor.appealScore)}
        </TooltipTrigger>
        <TooltipContent className="bg-black text-xs text-white">
          {mentor.appealSelections} / {mentor.appealAppearances}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function D3Retention({ mentor }: { mentor: MentorMetricsRow }) {
  if (mentor.d3RetentionRate === null || !mentor.d3Reliable) {
    return <LowDataValue>—</LowDataValue>;
  }

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger className="cursor-help">
          {formatPercent(mentor.d3RetentionRate)}
        </TooltipTrigger>
        <TooltipContent className="bg-black text-xs text-white">
          {mentor.d3Returned} / {mentor.d3CohortSize}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function MentorCell({ mentor }: { mentor: MentorMetricsRow }) {
  const { track } = useMentorPerformanceEvent();

  return (
    <Link
      to="/users/$userId"
      params={{ userId: mentor.mentorId }}
      search={{ tab: "performance" }}
      className="group flex items-center gap-3"
      onClick={() =>
        track(PostHogEvents.MENTOR_DASHBOARD_MENTOR_PROFILE_CLICKED, {
          mentor_id: mentor.mentorId,
        })
      }
    >
      <MentorProfileContent mentor={mentor} />
    </Link>
  );
}

function MentorProfileContent({ mentor }: { mentor: MentorMetricsRow }) {
  // The API returns the raw S3 location, which is only viewable once signed.
  const { url: profileImageUrl } = useSignedMedia(mentor.profileImageUrl);

  return (
    <>
      <Avatar className="size-10 border border-surfaces-z5 ring-offset-2 transition group-hover:ring-1 group-hover:ring-black/20">
        <AvatarImage src={profileImageUrl} />
        <AvatarFallback>{getInitials(mentor.mentorName)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="truncate font-heading text-[15px] font-bold leading-5 text-black underline-offset-2 group-hover:underline">
          {mentor.mentorName}
        </p>
        {isNewHire(mentor) && (
          <div className="flex items-center gap-1 text-[11px] leading-4 text-brand-violet">
            <span
              aria-hidden
              className="size-[13px] shrink-0 bg-brand-violet"
              style={{
                maskImage: `url(${Moodies.newSmile})`,
                maskSize: "contain",
                maskRepeat: "no-repeat",
                maskPosition: "center",
                WebkitMaskImage: `url(${Moodies.newSmile})`,
                WebkitMaskSize: "contain",
                WebkitMaskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
              }}
            />
            <span>New hire</span>
          </div>
        )}
      </div>
    </>
  );
}

export function MentorsTable({
  data,
  isLoading,
  isRefreshing = false,
  isError,
  sort,
  page,
  pageSize,
  pageSizes,
  onSortChange,
  onPageChange,
  onPageSizeChange,
}: MentorsTableProps) {
  const { track } = useMentorPerformanceEvent();
  const [messageMentor, setMessageMentor] = useState<MentorMetricsRow | null>(
    null,
  );

  const handleSort = (field: MentorMetricsSortField) => {
    const currentSort = sort.find((item) => item.field === field);
    const nextSort = nextMentorMetricsSort(sort, field);
    onSortChange(nextSort);

    if (!currentSort) {
      track(PostHogEvents.MENTOR_DASHBOARD_TABLE_SORTED, {
        column: field,
        sort_direction: "ascending",
      });
      return;
    }

    if (currentSort.direction === "asc") {
      track(PostHogEvents.MENTOR_DASHBOARD_TABLE_SORTED, {
        column: field,
        sort_direction: "descending",
      });
    }
  };

  if (isError) {
    return (
      <div className="flex min-h-64 w-full items-center justify-center rounded-md border border-surfaces-z5 bg-surfaces-z0 text-body-2 text-text-secondary">
        Failed to load mentor table
      </div>
    );
  }

  if (isLoading || !data) {
    return <MentorsTableSkeleton />;
  }

  const totalPages = Math.max(1, data.pagination.totalPages);

  return (
    <div
      aria-busy={isRefreshing}
      className={cn(
        "relative w-full transition-opacity",
        isRefreshing && "opacity-60",
      )}
    >
      {isRefreshing && (
        <div className="pointer-events-none absolute right-3 top-3 z-10 flex items-center gap-2 rounded-md bg-surfaces-z0/90 px-2 py-1 text-body-3 text-text-secondary shadow-sm">
          <Loader2 className="size-3.5 animate-spin" aria-hidden />
          Updating…
        </div>
      )}
      <div className="w-full overflow-x-auto rounded-md border border-[#e2e2e2] bg-surfaces-z0">
        <table className="w-full min-w-[1020px] table-fixed border-collapse">
          <colgroup>
            <col className="w-[254px]" />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col className="w-[84px]" />
          </colgroup>
          <thead>
            <tr className="h-14 border-b border-[#e2e2e2] bg-white">
              {COLUMNS.map(({ label, field }) => {
                const columnSort = sort.find((item) => item.field === field);
                return (
                  <th
                    key={field}
                    aria-sort={
                      columnSort
                        ? columnSort.direction === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                    className={cn(
                      "whitespace-nowrap border-r border-[#e2e2e2] p-0 text-left font-body text-[12px] font-normal leading-[1.6] tracking-[0.24px] text-black/50",
                      columnSort && "bg-[#e2e2e2] text-black",
                    )}
                  >
                    <Button
                      type="button"
                      variant="ghost-black"
                      shape="square"
                      size="sm"
                      fullWidth
                      trailingIcon={
                        columnSort ? (
                          columnSort.direction === "asc" ? (
                            <ChevronUpIcon className="size-3" />
                          ) : (
                            <ChevronDownIcon className="size-3" />
                          )
                        ) : undefined
                      }
                      className="h-14 !justify-between rounded-none px-6 shadow-none hover:bg-transparent focus:ring-0"
                      labelClassName="font-body text-[12px] font-normal leading-[1.6] tracking-[0.24px] !text-inherit"
                      onClick={() => handleSort(field)}
                    >
                      {label}
                    </Button>
                  </th>
                );
              })}
              <th aria-label="Actions" className="border-r border-[#e2e2e2]" />
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="h-[432px] text-center text-body-2 text-text-tertiary"
                >
                  No mentors found
                </td>
              </tr>
            ) : (
              data.rows.map((mentor) => (
                <tr key={mentor.mentorId} className="h-[72px]">
                  <td className="px-6 py-2">
                    <MentorCell mentor={mentor} />
                  </td>
                  <td className="px-6 py-2 text-body-2 tracking-[0.02em]">
                    {formatMentorTenure(mentor.tenureDays, mentor.tenureMonths)}
                  </td>
                  <td className="px-6 py-2 text-body-2 tracking-[0.02em]">
                    <ProfileScore
                      mentor={mentor}
                      available={data.appealScoreAvailable}
                    />
                  </td>
                  <td className="px-6 py-2 text-body-2 tracking-[0.02em]">
                    {mentor.engagedTeens === null ||
                    mentor.activeCaseload === null ? (
                      <LowDataValue>—</LowDataValue>
                    ) : (
                      <>
                        {formatDecimal(mentor.engagedTeens, 0)}{" "}
                        <span className="text-black/45">
                          / {formatDecimal(mentor.activeCaseload, 0)}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="px-6 py-2 text-body-2 tracking-[0.02em]">
                    {mentor.averageResponseTimeSeconds === null ? (
                      <LowDataValue>—</LowDataValue>
                    ) : (
                      formatResponseTime(mentor.averageResponseTimeSeconds)
                    )}
                  </td>
                  <td className="px-6 py-2 text-body-2 tracking-[0.02em]">
                    <D3Retention mentor={mentor} />
                  </td>
                  <td className="px-6 py-2 text-right">
                    <IconButton
                      size="sm"
                      shape="round-rectangle"
                      variant="ghost-gray"
                      aria-label={`Send message to ${mentor.mentorName}`}
                      className="size-9 rounded-[10px] bg-[#e2e2e2] text-icon-secondary hover:bg-surfaces-z4"
                      icon={<Send className="size-4" />}
                      onClick={() => {
                        setMessageMentor(mentor);
                        track(
                          PostHogEvents.MENTOR_DASHBOARD_QUICK_MESSAGE_CLICKED,
                          {
                            mentor_id: mentor.mentorId,
                          },
                        );
                      }}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex h-[70px] items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[14px] leading-normal text-black">
            Rows per page
          </span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-auto gap-2.5 rounded-[4px] border-[#c7c7c7] bg-white px-4 py-0 text-[14px] leading-6 text-black focus:ring-0 focus:ring-offset-0 [&>svg]:size-3 [&>svg]:opacity-100">
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizes.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <nav
          aria-label="Mentor table pages"
          className="flex items-center gap-3"
        >
          <IconButton
            size="sm"
            shape="round-rectangle"
            variant="ghost-black"
            aria-label="Previous page"
            disabled={page <= 1}
            icon={<ArrowLeftIcon className="size-4" />}
            onClick={() => onPageChange(page - 1)}
            className="disabled:text-[#c7c7c7] disabled:opacity-100"
          />
          <span className="text-[14px] leading-normal text-black">
            Page {page} of {Math.max(totalPages, 1)}
          </span>
          <IconButton
            size="sm"
            shape="round-rectangle"
            variant="ghost-black"
            aria-label="Next page"
            disabled={page >= totalPages || totalPages === 0}
            icon={<ArrowRightIcon className="size-4" />}
            onClick={() => onPageChange(page + 1)}
            className="disabled:text-[#c7c7c7] disabled:opacity-100"
          />
        </nav>
      </div>

      {messageMentor && (
        <SendMessageModal
          mentorId={messageMentor.mentorId}
          mentorName={messageMentor.mentorName}
          open
          onOpenChange={(open) => {
            if (!open) setMessageMentor(null);
          }}
        />
      )}
    </div>
  );
}

const SKELETON_ROW_COUNT = 8;

function MentorsTableSkeleton() {
  return (
    <div aria-busy aria-label="Loading mentors" className="w-full">
      <div className="w-full overflow-x-auto rounded-md border border-[#e2e2e2] bg-surfaces-z0">
        <table className="w-full min-w-[1020px] table-fixed border-collapse">
          <colgroup>
            <col className="w-[254px]" />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col className="w-[84px]" />
          </colgroup>
          <thead>
            <tr className="h-14 border-b border-[#e2e2e2] bg-white">
              {COLUMNS.map(({ label, field }) => (
                <th
                  key={field}
                  className="whitespace-nowrap border-r border-[#e2e2e2] px-6 text-left font-body text-[12px] font-normal leading-[1.6] tracking-[0.24px] text-black/50"
                >
                  {label}
                </th>
              ))}
              <th aria-label="Actions" className="border-r border-[#e2e2e2]" />
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
              <tr key={index} className="h-[72px]">
                <td className="px-6 py-2">
                  <div className="flex items-center gap-3">
                    <Skeleton className="size-10 shrink-0 rounded-full" />
                    <Skeleton className="h-4 w-28" />
                  </div>
                </td>
                <td className="px-6 py-2">
                  <Skeleton className="h-4 w-16" />
                </td>
                <td className="px-6 py-2">
                  <Skeleton className="h-4 w-10" />
                </td>
                <td className="px-6 py-2">
                  <Skeleton className="h-4 w-8" />
                </td>
                <td className="px-6 py-2">
                  <Skeleton className="h-4 w-14" />
                </td>
                <td className="px-6 py-2">
                  <Skeleton className="h-4 w-12" />
                </td>
                <td className="px-6 py-2 text-right">
                  <Skeleton className="ml-auto size-9 rounded-[10px]" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex h-[70px] items-center justify-between">
        <Skeleton className="h-8 w-36" />
        <Skeleton className="h-8 w-28" />
      </div>
    </div>
  );
}
